// Período compartido de TODA la plataforma (política 2 de coherencia) — estado,
// derivación de rangos y la
// barra de filtros (presets + navegación ◀ ▶ + fechas libres del Rango).
//
// Extraído del Dashboard para que "Líneas de producción" (y las pantallas que
// vengan) usen EXACTAMENTE la misma semántica de períodos y comparaciones:
//   hoy → mismo día de la semana anterior · semana → semana anterior ·
//   mes → mes anterior a la misma altura · rango → bloque anterior del mismo
//   largo (regla F3: nunca "vs ayer" en rangos largos).

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { inicioSemanaOperativa, semanaOperativaKeyDe, type GranularidadDetalle } from 'schemas';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { useTenantConfig } from '../api/useTenantConfig';
import { PeriodoNav, shiftDias, shiftMeses } from './PeriodoNav';

export type Preset = 'hoy' | 'semana' | 'mes' | 'trimestre' | 'corte' | 'rango';

const PRESETS_VALIDOS: ReadonlySet<string> = new Set([
  'hoy',
  'semana',
  'mes',
  'trimestre',
  'corte',
  'rango',
]);

// ── Fechas (UTC, coherente con PeriodoNav) ──────────────────────────────────

export function parseUTC(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!));
}

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function hoyIso(): string {
  const now = new Date();
  return toIsoDate(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())));
}

/** ISO YYYY-MM-DD que además es una fecha CALENDARIO real (2026-02-31 no lo es). */
export function esIsoValida(iso: string | null | undefined): iso is string {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
  const d = parseUTC(iso);
  return !Number.isNaN(d.getTime()) && toIsoDate(d) === iso;
}

/** Primer día de la semana operativa que contiene `iso` (con 1 = lunes ISO). */
function inicioSemanaDe(iso: string, diaInicioSemana = 1): string {
  return inicioSemanaOperativa(iso.slice(0, 10), diaInicioSemana);
}

function primerDiaMesDe(iso: string): string {
  return `${iso.slice(0, 7)}-01`;
}

function finDeMes(iso: string): string {
  const d = parseUTC(primerDiaMesDe(iso));
  d.setUTCMonth(d.getUTCMonth() + 1, 0);
  return toIsoDate(d);
}

/** Número de semana (operativa) de una fecha. Con default (1) = semana ISO. */
export function numeroSemanaIso(iso: string, diaInicioSemana = 1): number {
  return Number(semanaOperativaKeyDe(iso.slice(0, 10), diaInicioSemana).slice(-2));
}

/** Clave de semana 'IYYY-Wxx' de una fecha (comparable lexicográficamente).
 *  Con `diaInicioSemana` ≠ 1 es la clave de la SEMANA OPERATIVA del tenant. */
export function semanaIsoKeyDe(iso: string, diaInicioSemana = 1): string {
  return semanaOperativaKeyDe(iso.slice(0, 10), diaInicioSemana);
}

export const FMT_DIA = new Intl.DateTimeFormat('es-CO', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
});
export const FMT_DIA_CORTO = new Intl.DateTimeFormat('es-CO', {
  weekday: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});
export const FMT_DIA_MES = new Intl.DateTimeFormat('es-CO', {
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
});
export const FMT_MES = new Intl.DateTimeFormat('es-CO', {
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});
const FMT_WEEKDAY = new Intl.DateTimeFormat('es-CO', { weekday: 'long', timeZone: 'UTC' });

export function limpiar(s: string): string {
  return s.replace(/\./g, '').replace(/,/g, '').trim();
}

// ── Cortes de facturación (Q1 = 1–15 · Q2 = 16–22 · Q3 = 23–fin) ───────────

export function corteDe(iso: string): { mes: string; q: number } {
  const dia = Number(iso.slice(8, 10));
  return { mes: iso.slice(0, 7), q: dia <= 15 ? 1 : dia <= 22 ? 2 : 3 };
}

export function rangoDeCorte(mes: string, q: number): { desde: string; hasta: string } {
  const ano = Number(mes.slice(0, 4));
  const mm = Number(mes.slice(5, 7));
  const ultimoDia = new Date(Date.UTC(ano, mm, 0)).getUTCDate();
  const m2 = String(mm).padStart(2, '0');
  if (q === 1) return { desde: `${ano}-${m2}-01`, hasta: `${ano}-${m2}-15` };
  if (q === 2) return { desde: `${ano}-${m2}-16`, hasta: `${ano}-${m2}-22` };
  return { desde: `${ano}-${m2}-23`, hasta: `${ano}-${m2}-${String(ultimoDia).padStart(2, '0')}` };
}

export function corteVecino(mes: string, q: number, dir: 1 | -1): { mes: string; q: number } {
  let nuevoQ = q + dir;
  let [ano, mm] = [Number(mes.slice(0, 4)), Number(mes.slice(5, 7))];
  if (nuevoQ < 1) {
    nuevoQ = 3;
    mm -= 1;
    if (mm < 1) {
      mm = 12;
      ano -= 1;
    }
  } else if (nuevoQ > 3) {
    nuevoQ = 1;
    mm += 1;
    if (mm > 12) {
      mm = 1;
      ano += 1;
    }
  }
  return { mes: `${ano}-${String(mm).padStart(2, '0')}`, q: nuevoQ };
}

// ── Derivación del período ──────────────────────────────────────────────────

export interface PeriodoDerivado {
  desde: string;
  hasta: string;
  desdeAnt: string;
  hastaAnt: string;
  label: string;
  deltaLabel: string;
  isCurrent: boolean;
  /**
   * "parcial · N de M días" cuando el período está EN CURSO y aún no está
   * completo (política 2: el rango resuelto siempre visible y honesto).
   * null en 'hoy', en 'rango' y en períodos ya cerrados.
   */
  parcialLabel: string | null;
}

/** Días calendario inclusivos entre dos ISO. */
function diasEntre(desde: string, hasta: string): number {
  return (parseUTC(hasta).getTime() - parseUTC(desde).getTime()) / 86_400_000 + 1;
}

function parcialDe(esActual: boolean, desde: string, hasta: string, totalDias: number) {
  const dias = diasEntre(desde, hasta);
  return esActual && dias < totalDias ? `parcial · ${dias} de ${totalDias} días` : null;
}

export function derivarPeriodo(
  preset: Preset,
  anchor: string,
  rangoSel: { desde: string; hasta: string },
  diaInicioSemana = 1,
  hoy: string = hoyIso(),
): PeriodoDerivado {
  if (preset === 'rango') {
    const desde = rangoSel.desde <= rangoSel.hasta ? rangoSel.desde : rangoSel.hasta;
    const hasta = rangoSel.desde <= rangoSel.hasta ? rangoSel.hasta : rangoSel.desde;
    const dias = (parseUTC(hasta).getTime() - parseUTC(desde).getTime()) / 86_400_000 + 1;
    return {
      desde,
      hasta,
      desdeAnt: shiftDias(desde, -dias),
      hastaAnt: shiftDias(hasta, -dias),
      label: `${limpiar(FMT_DIA_MES.format(parseUTC(desde)))} – ${limpiar(FMT_DIA_MES.format(parseUTC(hasta)))}`,
      deltaLabel: `vs ${dias === 1 ? 'día anterior' : `${dias} días anteriores`}`,
      isCurrent: hasta >= hoy,
      parcialLabel: null,
    };
  }
  if (preset === 'corte') {
    const { mes, q } = corteDe(anchor);
    const r = rangoDeCorte(mes, q);
    const esActual = hoy >= r.desde && hoy <= r.hasta;
    const hasta = esActual ? hoy : r.hasta;
    // Corte anterior "a la misma altura" (mismos días transcurridos, con tope)
    const prev = corteVecino(mes, q, -1);
    const rAnt = rangoDeCorte(prev.mes, prev.q);
    const diasTrans = (parseUTC(hasta).getTime() - parseUTC(r.desde).getTime()) / 86_400_000;
    // Corte CERRADO → el anterior completo; en curso → "a la misma altura" (F1.3).
    const hastaAntCrudo = shiftDias(rAnt.desde, diasTrans);
    const hastaAnt = !esActual || hastaAntCrudo > rAnt.hasta ? rAnt.hasta : hastaAntCrudo;
    const mesAntCorto =
      limpiar(FMT_DIA_MES.format(parseUTC(rAnt.desde)))
        .split(' ')
        .pop() ?? '';
    return {
      desde: r.desde,
      hasta,
      desdeAnt: rAnt.desde,
      hastaAnt,
      label: `Corte Q${q} · ${limpiar(FMT_DIA_MES.format(parseUTC(r.desde)))} – ${limpiar(FMT_DIA_MES.format(parseUTC(r.hasta)))}`,
      deltaLabel: `vs Q${prev.q} ${mesAntCorto}${esActual ? ' a la misma altura' : ''}`,
      isCurrent: esActual,
      parcialLabel: parcialDe(esActual, r.desde, hasta, diasEntre(r.desde, r.hasta)),
    };
  }
  if (preset === 'hoy') {
    const esHoy = anchor === hoy;
    return {
      desde: anchor,
      hasta: anchor,
      desdeAnt: shiftDias(anchor, -7),
      hastaAnt: shiftDias(anchor, -7),
      label: `${esHoy ? 'Hoy · ' : ''}${limpiar(FMT_DIA.format(parseUTC(anchor)))}`,
      deltaLabel: `vs ${FMT_WEEKDAY.format(parseUTC(anchor))} anterior`,
      isCurrent: esHoy,
      parcialLabel: null,
    };
  }
  if (preset === 'semana') {
    const inicio = inicioSemanaDe(anchor, diaInicioSemana);
    const esActual = inicio === inicioSemanaDe(hoy, diaInicioSemana);
    const hasta = esActual ? hoy : shiftDias(inicio, 6);
    const n = numeroSemanaIso(inicio, diaInicioSemana);
    return {
      desde: inicio,
      hasta,
      desdeAnt: shiftDias(inicio, -7),
      hastaAnt: shiftDias(hasta, -7),
      label: `Semana ${n} · ${limpiar(FMT_DIA_MES.format(parseUTC(inicio)))} – ${limpiar(FMT_DIA_MES.format(parseUTC(hasta)))}`,
      deltaLabel: n > 1 ? `vs semana ${n - 1}` : 'vs semana anterior',
      isCurrent: esActual,
      parcialLabel: parcialDe(esActual, inicio, hasta, 7),
    };
  }
  if (preset === 'trimestre') {
    // Trimestre CALENDARIO (Q1 ene–mar … Q4 oct–dic), to-date como el mes:
    // el trimestre en curso corta en hoy y compara "a la misma altura".
    const anio = Number(anchor.slice(0, 4));
    const t = Math.floor((Number(anchor.slice(5, 7)) - 1) / 3); // 0..3
    const dia1 = `${anio}-${String(t * 3 + 1).padStart(2, '0')}-01`;
    const fin = finDeMes(`${anio}-${String(t * 3 + 3).padStart(2, '0')}-01`);
    const esActual = hoy >= dia1 && hoy <= fin;
    const hasta = esActual ? hoy : fin;
    const dia1Ant = shiftMeses(dia1, -3);
    const finAnt = finDeMes(shiftMeses(dia1Ant, 2));
    const diasTrans = (parseUTC(hasta).getTime() - parseUTC(dia1).getTime()) / 86_400_000;
    const hastaAntCrudo = shiftDias(dia1Ant, diasTrans);
    return {
      desde: dia1,
      hasta,
      desdeAnt: dia1Ant,
      // Trimestre CERRADO → anterior completo; en curso → misma altura (F1.3).
      hastaAnt: !esActual || hastaAntCrudo > finAnt ? finAnt : hastaAntCrudo,
      label: `Trimestre ${t + 1} · ${limpiar(FMT_DIA_MES.format(parseUTC(dia1)))} – ${limpiar(FMT_DIA_MES.format(parseUTC(fin)))}`,
      deltaLabel: `vs trimestre anterior${esActual ? ' a la misma altura' : ''}`,
      isCurrent: esActual,
      parcialLabel: parcialDe(esActual, dia1, hasta, diasEntre(dia1, fin)),
    };
  }
  const dia1 = primerDiaMesDe(anchor);
  const esActual = dia1 === primerDiaMesDe(hoy);
  const hasta = esActual ? hoy : finDeMes(dia1);
  const dia1Ant = shiftMeses(dia1, -1);
  const diasTranscurridos = (parseUTC(hasta).getTime() - parseUTC(dia1).getTime()) / 86_400_000;
  // Mes CERRADO → el anterior completo (febrero vs enero: 31 días, no 28); en
  // curso → "a la misma altura" (F1.3).
  const hastaAntCrudo = shiftDias(dia1Ant, diasTranscurridos);
  const hastaAnt =
    !esActual || hastaAntCrudo > finDeMes(dia1Ant) ? finDeMes(dia1Ant) : hastaAntCrudo;
  const mesAntLabel = FMT_MES.format(parseUTC(dia1Ant)).split(' ')[0] ?? 'mes anterior';
  return {
    desde: dia1,
    hasta,
    desdeAnt: dia1Ant,
    hastaAnt,
    label: FMT_MES.format(parseUTC(dia1)).replace(/^./, (c) => c.toUpperCase()),
    deltaLabel: `vs ${mesAntLabel}${esActual ? ' a la misma altura' : ''}`,
    isCurrent: esActual,
    parcialLabel: parcialDe(esActual, dia1, hasta, diasEntre(dia1, finDeMes(dia1))),
  };
}

// Granularidades del gráfico central por preset (decisión 2026-07-04).
export const GRANULARIDADES: Record<
  Preset,
  Array<{ value: GranularidadDetalle; label: string }>
> = {
  hoy: [{ value: 'hora', label: 'Hora' }],
  semana: [
    { value: 'dia', label: 'Día' },
    { value: 'hora_dia', label: 'Hora del día' },
  ],
  mes: [
    { value: 'dia', label: 'Día' },
    { value: 'semana', label: 'Semana' },
    { value: 'hora_dia', label: 'Hora del día' },
  ],
  trimestre: [
    { value: 'dia', label: 'Día' },
    { value: 'semana', label: 'Semana' },
    { value: 'hora_dia', label: 'Hora del día' },
  ],
  corte: [
    { value: 'dia', label: 'Día' },
    { value: 'hora_dia', label: 'Hora del día' },
  ],
  rango: [
    { value: 'dia', label: 'Día' },
    { value: 'semana', label: 'Semana' },
    { value: 'hora_dia', label: 'Hora del día' },
  ],
};

/** Etiqueta de bucket SEGÚN LA GRANULARIDAD DE LA RESPUESTA (defensivo: con
 *  keepPreviousData el estado puede ir adelante de los datos). */
export function labelBucket(gran: GranularidadDetalle, bucket: string): string {
  if (gran === 'dia') {
    const d = parseUTC(bucket);
    return Number.isNaN(d.getTime()) ? bucket : limpiar(FMT_DIA_CORTO.format(d));
  }
  if (gran === 'semana') {
    const n = Number(bucket.slice(-2));
    return Number.isNaN(n) ? bucket : `S${n}`;
  }
  return bucket; // hora / hora_dia → "06".."23"
}

// ── Hook de estado + barra de filtros ───────────────────────────────────────

const PRESETS_TODOS: Array<{ value: Preset; label: string }> = [
  { value: 'hoy', label: 'Hoy' },
  { value: 'semana', label: 'Esta semana' },
  { value: 'mes', label: 'Este mes' },
  { value: 'trimestre', label: 'Trimestre' },
  { value: 'corte', label: 'Corte Q' },
  { value: 'rango', label: 'Rango' },
];

// Set original del directivo (sin Trimestre) — default de BarraPeriodo para no
// cambiarle el comportamiento a nadie que no lo pida.
const PRESETS_DEFAULT: Preset[] = ['hoy', 'semana', 'mes', 'corte', 'rango'];

/**
 * Params del período EXACTAMENTE como los escribe `usePeriodo({ url: true })`
 * (?p=&ancla= · ?p=rango&desde=&hasta=). Sobre una copia de `base` si se pasa:
 * los demás params se conservan y los del otro modo se limpian. Sirve tanto
 * para persistir el estado como para armar enlaces internos que hereden el
 * contexto (drill-down, migas).
 */
export function paramsDePeriodo(
  preset: Preset,
  anchor: string,
  rangoSel: { desde: string; hasta: string },
  base?: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams(base);
  next.set('p', preset);
  if (preset === 'rango') {
    next.delete('ancla');
    next.set('desde', rangoSel.desde);
    next.set('hasta', rangoSel.hasta);
  } else {
    next.delete('desde');
    next.delete('hasta');
    next.set('ancla', anchor);
  }
  return next;
}

/** `path?p=…&ancla=…` listo para `<Link to>` / `navigate` con el período actual. */
export function enlaceConPeriodo(
  path: string,
  ctrl: { preset: Preset; anchor: string; rangoSel: { desde: string; hasta: string } },
): string {
  return `${path}?${paramsDePeriodo(ctrl.preset, ctrl.anchor, ctrl.rangoSel).toString()}`;
}

export interface UsePeriodoOpts {
  /**
   * Persistir preset/ancla/rango en el querystring (?p=&ancla=&desde=&hasta=)
   * con history replace: refrescar o volver conserva el período, y el enlace
   * es compartible (política 2.2). Los demás params de la URL se respetan.
   */
  url?: boolean;
}

export function usePeriodo(
  onPresetChange?: (p: Preset) => void,
  presetInicial: Preset = 'hoy',
  opts: UsePeriodoOpts = {},
) {
  const { diaInicioSemana, hoyOperativo } = useTenantConfig();
  const [sp, setSp] = useSearchParams();
  const spInicial = useRef(sp);
  const [preset, setPreset] = useState<Preset>(() => {
    const p = opts.url ? spInicial.current.get('p') : null;
    return p && PRESETS_VALIDOS.has(p) ? (p as Preset) : presetInicial;
  });
  const [anchor, setAnchor] = useState<string>(() => {
    const a = opts.url ? spInicial.current.get('ancla') : null;
    return esIsoValida(a) ? a : hoyIso();
  });
  const [rangoSel, setRangoSel] = useState<{ desde: string; hasta: string }>(() => {
    const d = opts.url ? spInicial.current.get('desde') : null;
    const h = opts.url ? spInicial.current.get('hasta') : null;
    if (esIsoValida(d) && esIsoValida(h)) {
      // Rango invertido en la URL → se normaliza (derivarPeriodo también lo tolera).
      return d <= h ? { desde: d, hasta: h } : { desde: h, hasta: d };
    }
    return { desde: shiftDias(hoyIso(), -6), hasta: hoyIso() };
  });

  // Escribir el estado a la URL (replace: no ensucia el historial). Solo los
  // params del período — el resto (filtros de la página) se conserva.
  useEffect(() => {
    if (!opts.url) return;
    setSp((prev) => paramsDePeriodo(preset, anchor, rangoSel, prev), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.url, preset, anchor, rangoSel.desde, rangoSel.hasta]);

  // URL → estado (F1.3): si la ubicación cambia sin desmontar la pantalla
  // (atrás/adelante del navegador, enlace interno con otro período), el estado
  // sigue a la URL. Valores iguales no disparan set → sin bucle con el efecto
  // de escritura de arriba.
  useEffect(() => {
    if (!opts.url) return;
    const p = sp.get('p');
    if (p && PRESETS_VALIDOS.has(p) && p !== preset) setPreset(p as Preset);
    const a = sp.get('ancla');
    if (esIsoValida(a) && a !== anchor) setAnchor(a);
    const d = sp.get('desde');
    const h = sp.get('hasta');
    if (esIsoValida(d) && esIsoValida(h)) {
      const nd = d <= h ? d : h;
      const nh = d <= h ? h : d;
      if (nd !== rangoSel.desde || nh !== rangoSel.hasta) setRangoSel({ desde: nd, hasta: nh });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  // El ancla arranca con la fecha del navegador; cuando /api/me trae el día
  // OPERATIVO (puede diferir en la franja nocturna bajo dia_fin), se corrige
  // una sola vez — solo si el usuario no navegó a otra fecha.
  useEffect(() => {
    setAnchor((a) => (a === hoyIso() && hoyOperativo !== a ? hoyOperativo : a));
  }, [hoyOperativo]);

  const periodo = useMemo(
    () => derivarPeriodo(preset, anchor, rangoSel, diaInicioSemana, hoyOperativo),
    [preset, anchor, rangoSel, diaInicioSemana, hoyOperativo],
  );

  const cambiarPreset = (p: Preset) => {
    setPreset(p);
    setAnchor(hoyOperativo);
    onPresetChange?.(p);
  };
  const shiftAnchor = (dir: 1 | -1) => {
    if (preset === 'corte') {
      const { mes, q } = corteDe(anchor);
      const vecino = corteVecino(mes, q, dir);
      setAnchor(rangoDeCorte(vecino.mes, vecino.q).desde);
      return;
    }
    if (preset === 'rango') {
      const dias =
        (parseUTC(periodo.hasta).getTime() - parseUTC(periodo.desde).getTime()) / 86_400_000 + 1;
      setRangoSel({
        desde: shiftDias(periodo.desde, dir * dias),
        hasta: shiftDias(periodo.hasta, dir * dias),
      });
      return;
    }
    if (preset === 'mes') setAnchor(shiftMeses(anchor, dir));
    else if (preset === 'trimestre') setAnchor(shiftMeses(anchor, dir * 3));
    else setAnchor(shiftDias(anchor, preset === 'hoy' ? dir : dir * 7));
  };
  const resetPeriodo = () => {
    if (preset === 'rango')
      setRangoSel({ desde: shiftDias(hoyOperativo, -6), hasta: hoyOperativo });
    else setAnchor(hoyOperativo);
  };

  return {
    preset,
    cambiarPreset,
    anchor,
    setAnchor,
    rangoSel,
    setRangoSel,
    periodo,
    shiftAnchor,
    resetPeriodo,
    hoy: hoyOperativo,
  };
}

export function BarraPeriodo({
  ctrl,
  presets = PRESETS_DEFAULT,
}: {
  ctrl: ReturnType<typeof usePeriodo>;
  /** Presets visibles, en orden. Default: el set histórico del directivo. */
  presets?: Preset[];
}) {
  const { preset, cambiarPreset, rangoSel, setRangoSel, periodo, shiftAnchor, resetPeriodo } = ctrl;
  const opciones = PRESETS_TODOS.filter((p) => presets.includes(p.value));
  return (
    <>
      <SegmentedControl value={preset} onChange={cambiarPreset} options={opciones} size="sm" />
      <PeriodoNav
        label={periodo.label}
        isCurrent={periodo.isCurrent}
        canNext={!periodo.isCurrent}
        onPrev={() => shiftAnchor(-1)}
        onNext={() => shiftAnchor(1)}
        onReset={resetPeriodo}
      />
      {periodo.parcialLabel ? (
        <span
          className="inline-flex items-center rounded-full bg-warn-soft px-2 py-0.5 text-[11px] font-semibold text-warn"
          title="Período en curso: los comparativos se hacen a la misma altura (to-date)"
        >
          {periodo.parcialLabel}
        </span>
      ) : null}
      {preset === 'rango' ? (
        <div className="flex items-center gap-1.5">
          <input
            type="date"
            value={rangoSel.desde}
            max={ctrl.hoy}
            onChange={(e) => e.target.value && setRangoSel({ ...rangoSel, desde: e.target.value })}
            className="h-8 rounded border border-border bg-surface-input px-2 text-xs text-fg"
            aria-label="Desde"
          />
          <span className="text-xs text-fg-tertiary">–</span>
          <input
            type="date"
            value={rangoSel.hasta}
            max={ctrl.hoy}
            onChange={(e) => e.target.value && setRangoSel({ ...rangoSel, hasta: e.target.value })}
            className="h-8 rounded border border-border bg-surface-input px-2 text-xs text-fg"
            aria-label="Hasta"
          />
        </div>
      ) : null}
    </>
  );
}
